import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { createStore, Provider } from 'jotai'
import {
  currentSiteAtom,
  globalSettingsAtom,
  sitesAtom,
  siteSlugAtom,
} from '../../lib/atoms'

const findCurrentSite = (sites, siteSlug) => {
  if (!sites || !siteSlug) return null
  return (
    sites.find((s) => {
      const pathParts = s.path.split('/').filter((p) => p !== '')
      return pathParts[pathParts.length - 1] === siteSlug
    }) ?? null
  )
}

// Every value in these atoms comes straight from page props, so rather than
// copying props into the global store in a useEffect (which leaves the atoms
// null/stale on the first render, during SSR, and for one render after a
// cross-site navigation), build a store pre-populated from the props during
// render and provide it to the page. Creating a store nobody is subscribed to
// yet is a pure operation, so this is safe to do in render. It also gives each
// SSR request its own store instead of sharing jotai's module-global default.
export default function StateInitializer({
  children,
  globalSettings,
  sites,
  siteSlug = null,
}) {
  const store = useMemo(() => {
    const s = createStore()
    s.set(globalSettingsAtom, globalSettings)
    s.set(sitesAtom, sites)
    s.set(siteSlugAtom, siteSlug)
    s.set(currentSiteAtom, findCurrentSite(sites, siteSlug))
    return s
  }, [globalSettings, sites, siteSlug])

  return <Provider store={store}>{children}</Provider>
}

StateInitializer.propTypes = {
  children: PropTypes.node.isRequired,
  globalSettings: PropTypes.shape({
    offices: PropTypes.object,
    mainMenu: PropTypes.object,
    footerMenu: PropTypes.object,
    globalOptions: PropTypes.object,
  }).isRequired,
  sites: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
      units: PropTypes.oneOf(['metric', 'imperial']),
      businessUnit: PropTypes.oneOf(['select', 'modellink']).isRequired,
    })
  ).isRequired,
  siteSlug: PropTypes.string,
}
